import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Archive, Trash2, ShieldAlert, Skull } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const LABEL = {
  users: "Usuarios", clientes: "Clientes", creditos: "Créditos", pagos: "Pagos",
  documentos: "Documentos",
};

// modo: "archivar" | "purgar"
export function EliminarEntidadDialog({ open, onOpenChange, entityId, nombre, onDone }) {
  const [modo, setModo] = useState("archivar");
  const [impacto, setImpacto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmTexto, setConfirmTexto] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setImpacto(null); setConfirmTexto(""); setModo("archivar"); return; }
    setLoading(true);
    api.get(`/admin/entidades/${entityId}/impacto-borrado`)
      .then(({ data }) => setImpacto(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, entityId]);

  const nombreReal = impacto?.nombre || nombre || "";
  const esProd = impacto?.entorno !== "demo";
  // Fraseo exigido por el backend, dependiente del entorno (feb-2026):
  //  · demo       → "CONFIRMAR"
  //  · produccion → "<NOMBRE COMERCIAL> ELIMINAR"
  const fraseoEsperado = impacto?.confirmacion_esperada
    || (esProd && nombreReal ? `${nombreReal} ELIMINAR` : "CONFIRMAR");
  // Para modo archivar seguimos aceptando la palabra CONFIRMAR (no destructivo).
  // Para modo purgar exigimos el fraseo del entorno.
  const norm = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();
  const confirmOk = modo === "purgar"
    ? norm(confirmTexto) === norm(fraseoEsperado)
    : norm(confirmTexto) === "confirmar";

  const ejecutar = async () => {
    if (!confirmOk) return;
    setBusy(true);
    try {
      if (modo === "purgar") {
        const { data } = await api.delete(`/admin/entidades/${entityId}/purgar`, {
          data: { confirmacion: confirmTexto.trim() },
        });
        toast.success(`Entidad eliminada. ${data.total_documentos} registros y ${data.archivos_purgados} archivos purgados.`);
      } else {
        const { data } = await api.post(`/admin/entidades/${entityId}/archivar`, {
          confirmacion: "CONFIRMAR",
        });
        toast.success(`Entidad archivada. ${data.usuarios_desactivados} usuario(s) desactivado(s).`);
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const conteosDetalle = impacto ? Object.entries(impacto.conteos || {})
    .filter(([k]) => !LABEL[k])
    .sort((a, b) => b[1] - a[1]) : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid="eliminar-entidad-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldAlert className="h-5 w-5 text-red-600" /> Eliminar / archivar entidad
          </DialogTitle>
          <DialogDescription>
            Operación de plataforma. Revisa el impacto y confirma escribiendo el fraseo requerido.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Calculando impacto…
          </div>
        ) : impacto ? (
          <div className="space-y-4">
            {/* Selector de modo */}
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="modo-archivar" onClick={() => { setModo("archivar"); setConfirmTexto(""); }}
                className={`rounded-lg border p-3 text-left text-sm transition-colors ${modo === "archivar" ? "border-accent bg-forest-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className="flex items-center gap-1.5 font-semibold text-slate-800"><Archive className="h-4 w-4" /> Archivar</span>
                <span className="mt-1 block text-xs text-slate-500">Conserva datos (retención 10 años) y desactiva el acceso. Reversible.</span>
              </button>
              <button data-testid="modo-purgar" onClick={() => { setModo("purgar"); setConfirmTexto(""); }}
                className={`rounded-lg border p-3 text-left text-sm transition-colors ${modo === "purgar" ? "border-red-300 bg-red-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className="flex items-center gap-1.5 font-semibold text-slate-800"><Trash2 className="h-4 w-4" /> Eliminar (purga total)</span>
                <span className="mt-1 block text-xs text-slate-500">Borra TODO + archivos de la entidad. Irreversible.</span>
              </button>
            </div>

            {/* Badge entorno */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500">Entorno:</span>
              <span data-testid="impacto-entorno" className={`rounded-full px-2 py-0.5 font-semibold ${impacto.entorno === "demo" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                {impacto.entorno === "demo" ? "Demo / prueba" : "Producción"}
              </span>
              <span className="text-slate-500">·</span>
              <span className="truncate font-medium text-slate-700">{nombreReal}</span>
            </div>

            {/* Aviso ROJO FUERTE para purga de producción (feb-2026) */}
            {modo === "purgar" && esProd && (
              <div data-testid="aviso-purga-produccion"
                className="rounded-lg border-2 border-red-500 bg-red-50 p-3.5 text-sm text-red-800 shadow-sm">
                <div className="mb-1.5 flex items-center gap-2 font-heading font-bold uppercase tracking-wide">
                  <Skull className="h-5 w-5" /> Esta entidad es de PRODUCCIÓN
                </div>
                <p className="leading-snug">
                  Estás por borrar <b>datos reales</b> de forma <b>permanente</b>.
                  Esta acción <b>NO se puede deshacer</b>. Verifica el nombre y el conteo antes de continuar.
                </p>
              </div>
            )}

            {/* Desglose de impacto */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="impacto-desglose">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {modo === "purgar" ? "Se eliminarán permanentemente:" : "Se conservarán (archivado), se desactivará el acceso:"}
              </p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                {Object.entries(impacto.destacados).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span className="truncate text-slate-600">{LABEL[k] || k}</span>
                    <span className="font-semibold tabular-nums text-slate-900" data-testid={`impacto-${k}`}>{v}</span>
                  </div>
                ))}
                <div className="flex justify-between gap-2">
                  <span className="truncate text-slate-600">Archivos</span>
                  <span className="font-semibold tabular-nums text-slate-900" data-testid="impacto-archivos">{impacto.archivos_gridfs}</span>
                </div>
              </div>
              {/* En producción con purga, expandimos todo el detalle por colección */}
              {modo === "purgar" && esProd && conteosDetalle.length > 0 && (
                <details className="mt-2 border-t border-slate-200 pt-2" data-testid="impacto-detalle-full">
                  <summary className="cursor-pointer text-xs text-slate-600 hover:text-slate-800">
                    Ver conteo por colección ({conteosDetalle.length} colecciones adicionales)
                  </summary>
                  <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    {conteosDetalle.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2 text-slate-500">
                        <span className="truncate">{k}</span>
                        <span className="font-semibold tabular-nums text-slate-700">{v}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <p className="mt-2 border-t border-slate-200 pt-2 text-xs text-slate-500">
                Total de registros asociados: <b className="text-slate-700" data-testid="impacto-total">{impacto.total_documentos}</b> en {Object.keys(impacto.conteos).length} colecciones.
              </p>
            </div>

            {modo === "purgar" && !esProd && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Esta acción es <b>irreversible</b> y purga todos los datos y archivos de <b>{nombreReal}</b>. Queda registrada en bitácora.
              </div>
            )}

            {/* Confirmación con fraseo dinámico por entorno */}
            <div>
              <Label htmlFor="confirm-texto" className="mb-1.5 block text-sm font-medium text-slate-700">
                Para confirmar, escribe exactamente{" "}
                <span data-testid="fraseo-esperado"
                  className={`inline-block rounded px-1.5 py-0.5 font-mono font-semibold ${modo === "purgar" && esProd ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-900"}`}>
                  {modo === "purgar" ? fraseoEsperado : "CONFIRMAR"}
                </span>
              </Label>
              <Input id="confirm-texto" data-testid="confirmacion-texto" value={confirmTexto}
                onChange={(e) => setConfirmTexto(e.target.value)}
                placeholder={modo === "purgar" ? fraseoEsperado : "CONFIRMAR"}
                autoComplete="off" spellCheck={false} />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" data-testid="eliminar-cancelar" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            data-testid="eliminar-confirmar"
            disabled={!confirmOk || busy}
            onClick={ejecutar}
            className={modo === "purgar" ? "bg-red-600 hover:bg-red-700" : "bg-accent hover:bg-forest-700"}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {modo === "purgar" ? "Eliminar definitivamente" : "Archivar entidad"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
